import React from "react";

import { FormattedMessage } from "react-intl";

import CheckCircleFilled from "@ps-design-system/icons/CheckCircleFilled/CheckCircleFilled24";
import InfoFilled from "@ps-design-system/icons/InfoFilled/InfoFilled24";

import { TAX_INFORMATION_STATUSES } from "../tax-information.constants";
import { getTaxInformationMetaById } from "../tax-information.utils";

/**
 * Get StatusBanner meta
 * @param {string} taxInformationStatus status of tax id in user's profile -> required
 * @param {string} taxInformationId id of tax or vat -> required
 * @returns {{
 *   icon: node,
 *   containerClassName: string,
 *   title: node,
 *   message: node
 * }} information you need to display in banner -> optional
 */
const getStatusBannerMeta = (taxInformationStatus, taxInformationId) => {
  const { shortNameMessageDescriptor, fullNameMessageDescriptor } =
    getTaxInformationMetaById(taxInformationId);

  if (!Object.values(TAX_INFORMATION_STATUSES).includes(taxInformationStatus)) {
    // Improve console error description if type is missing
    throw new Error(
      `[TaxInformationStatusBanner] invalid status "${taxInformationStatus}"`,
    );
  }

  return {
    saved: {
      icon: <CheckCircleFilled className="tw-text-green-800" />,
      containerClassName: "tw-bg-blue-100 tw-border-blue-800",
      title: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_on_file"
          defaultMessage="{taxIdShortName} on file"
          values={{
            taxIdShortName: (
              <FormattedMessage {...shortNameMessageDescriptor} />
            ),
          }}
        />
      ),
      message: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_on_file_description"
          defaultMessage="Your {taxIdShortName} has been securely submitted to our payment processor."
          values={{
            taxIdShortName: (
              <FormattedMessage {...shortNameMessageDescriptor} />
            ),
          }}
        />
      ),
    },
    required: {
      icon: <InfoFilled className="tw-text-blue-800" />,
      containerClassName: "tw-bg-blue-100 tw-border-blue-800",
      title: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_required"
          defaultMessage="{taxIdShortName} required"
          values={{
            taxIdShortName: (
              <FormattedMessage {...shortNameMessageDescriptor} />
            ),
          }}
        />
      ),
      message: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_required_description"
          defaultMessage="Please provide your {taxIdFullName}"
          values={{
            taxIdFullName: <FormattedMessage {...fullNameMessageDescriptor} />,
          }}
        />
      ),
    },
    not_saved: {
      icon: <InfoFilled className="tw-text-blue-800" />,
      containerClassName: "tw-bg-blue-100 tw-border-blue-800",
      title: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_not_saved"
          defaultMessage="{taxIdShortName} not saved"
          values={{
            taxIdShortName: (
              <FormattedMessage {...shortNameMessageDescriptor} />
            ),
          }}
        />
      ),
      message: (
        <FormattedMessage
          id="hosting.tax_information_status_banner.tax_id_required_description"
          defaultMessage="Please provide your {taxIdFullName}"
          values={{
            taxIdFullName: <FormattedMessage {...fullNameMessageDescriptor} />,
          }}
        />
      ),
    },
  }[taxInformationStatus];
};

export { getStatusBannerMeta };
